// Filename: Button.tsx
//
// Component: Button
//
// Purpose:
// Provide a reusable Button component built on top of MUI's Button.
// This component applies custom styles using the useButtonStyles hook,
// while still allowing variant and color configuration.
//
// What this file does:
// - Defines ButtonProps to type the accepted component props.
// - Imports MUI Button as the base UI element.
// - Imports custom style logic from ButtonStyles.ts.
// - Applies different styles depending on the selected variant.
// - Renders children inside the button.
//
// Notes:
// This component is part of the GMHut design system and is intended
// to keep styling consistent across the application.
//
// <param> variant: "text" | "contained"
//     - Determines the visual style of the button.
//
// <param> color: "primary" | "secondary"
//     - Intended to determine theme color styling (future use).
//
// <param> children: React.ReactNode
//     - Content displayed inside the button (text or nested elements).

import MUIButton from "@mui/material/Button"; // Import the base Material UI Button component
import { useButtonStyles } from "./ButtonStyles"; // Import custom style hook for this button

// ButtonProps type
// Defines the props accepted by this Button component.
type ButtonProps = {
  onClick?: () => void
  variant?: "text" | "contained"; // Button style variant
  color?: "primary" | "secondary"; // Theme color option (currently unused but reserved for expansion)
  children: React.ReactNode; // Content rendered inside the button
};

// Button component
// Wraps MUI Button and applies custom styling logic.
const Button = ({
  // Default props
  variant = "text", // Default to text button if no variant provided
  color = "primary", // Default to primary color
  onClick,
  children,
}: ButtonProps) => {
  // Fetch style classes from the styles hook.
  // These styles depend on the current MUI theme.
  const {classes} = useButtonStyles();

  return (
    <MUIButton
      // Apply style based on variant selection
      // If contained → use contained styles
      // Otherwise → use text styles
      className={variant === "contained" ? classes.contained : classes.text}

      // Pass variant down to MUI Button so it renders correctly
      variant={variant}
      onClick={onClick}
    >
      {/* Render button content */}
      {children}
    </MUIButton>
  );
};

export default Button; // Export component for use in app and Storybook
